/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Floating feedback widget that lets MVM users drag-select a page area and
 * submit a bug report or feature request. Submissions are routed through
 * resource.feedback.ts (server-side proxy) to the cc-mcp feedback store.
 */
import { useState, useEffect } from 'react';
import { getBasePath } from '@/lib/utils';

type Phase = 'idle' | 'selecting' | 'modal';
interface Rect {
    x: number;
    y: number;
    w: number;
    h: number;
}

export function FeedbackWidget() {
    const [phase, setPhase] = useState<Phase>('idle');
    const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
    const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null);
    const [selection, setSelection] = useState<Rect | null>(null);
    const [type, setType] = useState<'bug' | 'feature'>('bug');
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (phase === 'idle') return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') reset();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [phase]);

    function reset() {
        setPhase('idle');
        setDragStart(null);
        setDragCurrent(null);
        setSelection(null);
        setComment('');
        setType('bug');
        setError(null);
        setSuccess(false);
    }

    function handleMouseDown(e: React.MouseEvent) {
        e.preventDefault();
        setDragStart({ x: e.clientX, y: e.clientY });
        setDragCurrent({ x: e.clientX, y: e.clientY });
    }

    function handleMouseMove(e: React.MouseEvent) {
        if (!dragStart) return;
        setDragCurrent({ x: e.clientX, y: e.clientY });
    }

    function handleMouseUp(e: React.MouseEvent) {
        if (!dragStart) return;
        const x = Math.min(dragStart.x, e.clientX);
        const y = Math.min(dragStart.y, e.clientY);
        const w = Math.abs(e.clientX - dragStart.x);
        const h = Math.abs(e.clientY - dragStart.y);

        if (w < 10 || h < 10) {
            setDragStart(null);
            setDragCurrent(null);
            return;
        }

        setSelection({
            x: x / window.innerWidth,
            y: y / window.innerHeight,
            w: w / window.innerWidth,
            h: h / window.innerHeight,
        });
        setDragStart(null);
        setDragCurrent(null);
        setPhase('modal');
    }

    async function handleSubmit() {
        if (!comment.trim() || !selection) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetch(`${getBasePath()}/resource/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type,
                    comment: comment.trim(),
                    section: window.location.pathname,
                    sel_x: selection.x,
                    sel_y: selection.y,
                    sel_w: selection.w,
                    sel_h: selection.h,
                }),
            });
            if (!res.ok) throw new Error(`Server returned ${res.status}`);
            setSuccess(true);
            setTimeout(reset, 1500);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setSubmitting(false);
        }
    }

    const selRect =
        dragStart && dragCurrent
            ? {
                  left: Math.min(dragStart.x, dragCurrent.x),
                  top: Math.min(dragStart.y, dragCurrent.y),
                  width: Math.abs(dragCurrent.x - dragStart.x),
                  height: Math.abs(dragCurrent.y - dragStart.y),
              }
            : null;

    return (
        <>
            {phase === 'idle' && (
                <button
                    onClick={() => setPhase('selecting')}
                    className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary hover:bg-primary/90 active:bg-primary/80 text-primary-foreground text-sm font-medium shadow-lg transition-colors select-none">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor">
                        <path
                            fillRule="evenodd"
                            d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                            clipRule="evenodd"
                        />
                    </svg>
                    Feedback
                </button>
            )}

            {phase === 'selecting' && (
                <div
                    className="fixed inset-0 z-50 select-none"
                    style={{ cursor: 'crosshair', background: 'rgba(0,0,0,0.18)' }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}>
                    <div className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 bg-foreground/90 text-background text-sm px-4 py-2 rounded-full shadow-lg">
                        Drag to select an area · Esc to cancel
                    </div>
                    {selRect && (
                        <div
                            className="absolute border-2 border-primary/60 bg-primary/10 pointer-events-none"
                            style={selRect}
                        />
                    )}
                </div>
            )}

            {phase === 'modal' && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/60 p-4">
                    <div className="bg-background rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
                        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-foreground">Submit Feedback</h2>
                            <button
                                onClick={reset}
                                className="text-muted-foreground hover:text-foreground transition-colors"
                                aria-label="Close">
                                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path
                                        fillRule="evenodd"
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="px-5 py-4 space-y-3 overflow-y-auto">
                            <div className="rounded-lg border border-dashed border-border py-4 text-center text-xs text-muted-foreground">
                                Area selected · no screenshot captured
                            </div>

                            <div className="flex gap-2">
                                {(['bug', 'feature'] as const).map((t) => (
                                    <button
                                        key={t}
                                        onClick={() => setType(t)}
                                        className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                                            type === t
                                                ? t === 'bug'
                                                    ? 'bg-destructive border-destructive text-destructive-foreground'
                                                    : 'bg-primary border-primary text-primary-foreground'
                                                : 'border-border text-muted-foreground hover:bg-muted'
                                        }`}>
                                        {t === 'bug' ? '🐛 Bug' : '✨ Feature Request'}
                                    </button>
                                ))}
                            </div>

                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder={
                                    type === 'bug'
                                        ? 'Describe what went wrong...'
                                        : "Describe the improvement you'd like to see..."
                                }
                                rows={4}
                                className="w-full rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                            />

                            <p className="text-xs text-muted-foreground">
                                Page: <span className="font-medium text-foreground">{window.location.pathname}</span>
                            </p>

                            {error && <p className="text-xs text-destructive">{error}</p>}
                            {success && <p className="text-xs text-success font-medium">Feedback submitted!</p>}
                        </div>

                        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
                            <button
                                onClick={reset}
                                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting || !comment.trim() || success}
                                className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground rounded-lg transition-colors">
                                {submitting ? 'Submitting…' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
